/**
 * useOdometerCapture — Smart 3-tier odometer capture hook
 *
 * Tier 1: If the vehicle has GPS tracking (CarTrack etc.), fetch the live
 *         odometer value from the tracking provider.
 * Tier 2: If CarTrack fetch fails (or vehicle is untracked), require a photo
 *         of the odometer which is AI-verified in real time.
 * Tier 3: Manual numeric entry (always available as fallback; marked as
 *         'manual' source in the logbook for audit trail).
 *
 * All tiers are encapsulated in a single hook so every component that captures
 * odometer readings behaves identically.
 */

'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { authService } from '@/lib/authService';
import { useNetwork } from '@/lib/networkService';
import { compressImage } from '@/lib/compressImage';

// ============================================================================
// TYPES
// ============================================================================

export type OdometerSource =
  | 'tracking'       // Fetched from CarTrack / fleet tracker
  | 'photo_verified' // Captured via photo + AI verification
  | 'photo_pending_verification' // Photo captured offline, AI will verify after sync
  | 'manual';        // Typed in manually (untracked vehicle only)

export type OdometerCaptureStatus =
  | 'idle'
  | 'fetching_tracking'   // Hitting fleet API for live odometer
  | 'tracking_success'    // Got live value
  | 'tracking_failed'     // CarTrack failed → must take photo
  | 'awaiting_photo'      // User must take a photo
  | 'verifying_photo'     // AI analyzing photo
  | 'photo_verified'      // Photo OK → reading extracted
  | 'photo_pending_sync'  // Photo saved offline, waiting for deferred AI verification
  | 'photo_rejected'      // Photo not an odometer / low confidence
  | 'manual_entry'        // Manual fallback for untracked vehicles
  | 'complete';           // Final: value accepted

export interface OdometerVerification {
  detected_reading: number | null;
  confidence: number;
  is_odometer: boolean;
  validation: 'verified' | 'mismatch' | 'not_odometer' | 'low_confidence' | 'pending_offline' | 'api_error_fallback';
  validation_message: string;
}

export interface OdometerCaptureResult {
  reading: number;
  source: OdometerSource;
  photo?: string;           // base64 odometer photo (if tier 2)
  verification?: OdometerVerification;
  tracked_provider?: string;
  /** True when the reading came from a live tracker poll; false when it is
   *  the provider-synced stored value (e.g. last Cartrack sync) */
  live?: boolean;
}

export interface OdometerVehicleInfo {
  id?: string | null;
  registration?: string | null;
  fuel_type?: string | null;
  is_tracked: boolean;
  tracking_provider?: string | null;
  live_odometer_km?: number | null;
  stored_odometer_km?: number | null;
}

export interface OdometerPhotoVerificationResult {
  detected_reading: number | null;
  confidence: number;
  is_odometer: boolean;
  validation: 'verified' | 'mismatch' | 'not_odometer' | 'low_confidence' | 'pending_offline' | 'api_error_fallback';
  validation_message: string;
}

export interface UseOdometerCaptureOptions {
  /** Work-item UUID or external_id of the vehicle */
  vehicleId?: string | null;
  /** Optional label for AI context */
  vehicleName?: string;
  /** If true, starts the fetch immediately on mount */
  autoFetch?: boolean;
  /** When online, require tracker or photo/OCR before allowing a reading */
  requirePhotoWhenOnline?: boolean;
  /**
   * Allow manual numeric entry while online (default: true).
   * When true, the user can always type the odometer reading manually,
   * even if `requirePhotoWhenOnline` is set — photo capture is shown as
   * the preferred/encouraged path but manual entry remains available.
   * Set to false to force photo-only capture when online.
   */
  allowManualWhenOnline?: boolean;
  /**
   * Allow manual numeric entry when the vehicle IS tracked but the tracker
   * returned no usable odometer (default: false — 'tracking_failed' forces a
   * photo). When true, the flow falls through to the manual/photo tier so the
   * user can type the reading while still being offered the photo option.
   */
  allowManualOnTrackingFailure?: boolean;
  /** Allow manual numeric entry while offline */
  allowOfflineManualEntry?: boolean;
  /** Callback when the reading changes (any tier) */
  onReadingChange?: (reading: number | null) => void;
  /** Optional override for tracker lookup (useful for public token-based flows) */
  fetchVehicleInfo?: () => Promise<OdometerVehicleInfo | null>;
  /** Optional override for AI photo verification (useful for public token-based flows) */
  verifyPhoto?: (args: {
    image: string;
    expectedReading?: number | null;
    vehicleName?: string;
  }) => Promise<OdometerPhotoVerificationResult>;
}

export interface UseOdometerCaptureReturn {
  // ── State ────────────────────────────────────────
  status: OdometerCaptureStatus;
  source: OdometerSource | null;
  /** Current resolved reading (km), null if not yet captured */
  reading: number | null;
  /** True if the vehicle has GPS tracking */
  isTracked: boolean;
  /** Tracking provider name (e.g. "cartrack") */
  trackingProvider: string | null;
  /** True when the current reading came from a live tracker poll; false when
   *  it is the provider-synced stored value */
  readingIsLive: boolean;
  /** Photo base64 (if captured) */
  photo: string | null;
  /** AI verification result */
  verification: OdometerVerification | null;
  /** User-facing status message */
  statusMessage: string;
  /** True while any async operation is running */
  loading: boolean;
  /** Error message, if any */
  error: string | null;
  /** True when the reading is finalised and valid */
  isComplete: boolean;
  /** Whether manual entry is allowed (untracked vehicle) */
  allowManualEntry: boolean;

  // ── Actions ──────────────────────────────────────
  /** Start/restart the tracking fetch */
  fetchFromTracking: () => Promise<void>;
  /** Submit a photo for AI verification (base64) */
  submitPhoto: (base64: string) => Promise<void>;
  /** Manually set the reading (only for untracked vehicles) */
  setManualReading: (km: number) => void;
  /**
   * Update the reading live while the user is typing. Sets reading +
   * source so onReadingChange and getResult() reflect it immediately,
   * but does NOT transition status to 'complete' (which would unmount
   * the input mid-typing). Pass null when the input is cleared/invalid.
   */
  updateManualReading: (km: number | null) => void;
  /** Reset everything */
  reset: () => void;
  /** Build the final result object for embedding in form data */
  getResult: () => OdometerCaptureResult | null;
}

// ============================================================================
// HOOK
// ============================================================================

export function useOdometerCapture(opts: UseOdometerCaptureOptions = {}): UseOdometerCaptureReturn {
  const {
    vehicleId,
    vehicleName,
    autoFetch = true,
    requirePhotoWhenOnline = false,
    allowManualWhenOnline = true,
    allowManualOnTrackingFailure = false,
    allowOfflineManualEntry = true,
    onReadingChange,
    fetchVehicleInfo: fetchVehicleInfoOverride,
    verifyPhoto: verifyPhotoOverride,
  } = opts;
  const network = useNetwork();

  // ── Core state ──────────────────────────────────────────────────
  const [status, setStatus] = useState<OdometerCaptureStatus>('idle');
  const [source, setSource] = useState<OdometerSource | null>(null);
  const [reading, setReading] = useState<number | null>(null);
  const [isTracked, setIsTracked] = useState(false);
  const [trackingProvider, setTrackingProvider] = useState<string | null>(null);
  const [readingIsLive, setReadingIsLive] = useState(false);
  const [photo, setPhoto] = useState<string | null>(null);
  const [verification, setVerification] = useState<OdometerVerification | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Propagate reading changes
  useEffect(() => {
    onReadingChange?.(reading);
  }, [reading]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOffline = network.isConnected === false;

  const transitionToManualOrPhoto = useCallback((message?: string | null) => {
    setError(message || null);

    // Photo-only mode: when online and manual entry is explicitly disabled
    if (requirePhotoWhenOnline && !isOffline && !allowManualWhenOnline) {
      setStatus('awaiting_photo');
      return;
    }

    // Default: allow manual entry (with photo as optional enhancement)
    if (allowOfflineManualEntry || !requirePhotoWhenOnline || allowManualWhenOnline) {
      setStatus('manual_entry');
      return;
    }

    setStatus('awaiting_photo');
  }, [allowManualWhenOnline, allowOfflineManualEntry, isOffline, requirePhotoWhenOnline]);

  const transitionToManualApiFallback = useCallback((message: string) => {
    setIsTracked(false);
    setError(message);
    setStatus('manual_entry');
  }, []);

  // ── Helpers ─────────────────────────────────────────────────────
  const getAuthToken = () => {
    try {
      return authService.getTokens()?.access_token;
    } catch { return undefined; }
  };

  // ── Tier 1: Fetch from tracking ────────────────────────────────
  const fetchFromTracking = useCallback(async () => {
    if (!vehicleId && !fetchVehicleInfoOverride) {
      setIsTracked(false);
      transitionToManualOrPhoto(null);
      return;
    }

    setStatus('fetching_tracking');
    setError(null);

    try {
      let vehicleInfo: OdometerVehicleInfo | null = null;

      if (fetchVehicleInfoOverride) {
        vehicleInfo = await fetchVehicleInfoOverride();
      } else {
        const token = getAuthToken();
        const params = new URLSearchParams({
          action: 'get_vehicle_info',
          vehicle_id: vehicleId!,
        });
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        let resp: Response;
        try {
          resp = await fetch(`/api/v1/work-items/fleet?${params}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timeoutId);
        }

        if (!mountedRef.current) return;

        if (!resp.ok) {
          transitionToManualApiFallback(
            'Tracker service is temporarily unavailable. Enter the odometer manually so you can continue.',
          );
          return;
        }

        const data = await resp.json();
        if (data.success && data.vehicle) {
          vehicleInfo = data.vehicle as OdometerVehicleInfo;
        }
      }

      if (!mountedRef.current) return;

      if (!vehicleInfo) {
        transitionToManualApiFallback(
          'Vehicle tracking details are temporarily unavailable. Enter the odometer manually so you can continue.',
        );
        return;
      }

      const v = vehicleInfo;
      const tracked = !!v.is_tracked;
      const provider = v.tracking_provider || null;
      setIsTracked(tracked);
      setTrackingProvider(provider);

      if (tracked && v.live_odometer_km != null) {
        // Tier 1 success
        setReading(v.live_odometer_km);
        setReadingIsLive(true);
        setSource('tracking');
        setStatus('tracking_success');
        return;
      }

      if (tracked && v.live_odometer_km == null) {
        // Live poll returned nothing — if the API still has a synced
        // odometer for this tracked vehicle (e.g. last Cartrack sync),
        // that reading already came from the tracker, so accept it and
        // don't ask the user for input.
        if (v.stored_odometer_km != null) {
          setReading(v.stored_odometer_km);
          setReadingIsLive(false);
          setSource('tracking');
          setStatus('tracking_success');
          return;
        }
        // Tracked but CarTrack failed to return live value → photo required,
        // unless the caller opted in to a manual-entry fallback.
        if (allowManualOnTrackingFailure) {
          transitionToManualOrPhoto(
            'Could not retrieve live odometer from tracking. Take a photo of the odometer or enter it manually.',
          );
          return;
        }
        setStatus('tracking_failed');
        setError('Could not retrieve live odometer from tracking. Please take a photo of the odometer.');
        return;
      }

      // Not tracked — allow manual entry but also photo
      if (v.stored_odometer_km != null) {
        setReading(v.stored_odometer_km);
      }
      transitionToManualOrPhoto(
        requirePhotoWhenOnline && !isOffline
          ? 'This vehicle is not live-tracked. Take an odometer photo so AI can verify the reading.'
          : null,
      );
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const isAbort = err instanceof DOMException && err.name === 'AbortError';
      console.warn('[useOdometerCapture] Tracking fetch failed:', isAbort ? 'timeout (15s)' : err);
      transitionToManualApiFallback(
        isAbort
          ? 'Tracker service took too long to respond. Enter the odometer manually so you can continue.'
          : 'Could not reach the odometer services. Enter the reading manually so you can continue.',
      );
    }
  }, [allowManualOnTrackingFailure, fetchVehicleInfoOverride, isOffline, requirePhotoWhenOnline, transitionToManualApiFallback, transitionToManualOrPhoto, vehicleId]);

  // ── Tier 2: Photo + AI verification ────────────────────────────
  const submitPhoto = useCallback(async (base64: string) => {
    setStatus('verifying_photo');
    setPhoto(base64);
    setError(null);
    setVerification(null);

    const saveForDeferredVerification = (
      message: string,
      validation: OdometerVerification['validation'] = 'pending_offline',
    ) => {
      const pendingVerification: OdometerVerification = {
        detected_reading: reading,
        confidence: 0,
        is_odometer: true,
        validation,
        validation_message: message,
      };

      setVerification(pendingVerification);
      if (reading != null) {
        setSource('photo_pending_verification');
        setStatus('complete');
      } else {
        setStatus('photo_pending_sync');
      }
      setError(message);
    };

    try {
      // Compress before sending
      const compressed = await compressImage(base64, 1920, 0.8);

      if (isOffline) {
        saveForDeferredVerification('Photo saved offline. Enter the visible reading and AI will verify it after sync.');
        return;
      }

      let od: OdometerPhotoVerificationResult | null = null;

      if (verifyPhotoOverride) {
        od = await verifyPhotoOverride({
          image: compressed,
          expectedReading: reading,
          vehicleName,
        });
      } else {
        const token = getAuthToken();
        const resp = await fetch('/api/v1/work-items/fleet/verify-odometer', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            image: compressed,
            expected_reading: reading,
            vehicle_name: vehicleName,
          }),
        });

        if (!mountedRef.current) return;

        if (!resp.ok) {
          saveForDeferredVerification('Odometer verification service is temporarily unavailable. Enter the visible reading manually to continue.', 'api_error_fallback');
          return;
        }

        const data = await resp.json();
        if (data.success && data.odometer) {
          od = data.odometer as OdometerPhotoVerificationResult;
        }
      }

      if (!mountedRef.current) return;

      if (!od) {
        saveForDeferredVerification('We could not verify the photo right now. Enter the visible reading manually to continue.', 'api_error_fallback');
        return;
      }

      const verif: OdometerVerification = {
        detected_reading: od.detected_reading,
        confidence: od.confidence,
        is_odometer: od.is_odometer,
        validation: od.validation,
        validation_message: od.validation_message,
      };
      setVerification(verif);

      if (od.is_odometer && od.confidence >= 0.5 && od.detected_reading != null) {
        setReading(od.detected_reading);
        setSource('photo_verified');
        setStatus('photo_verified');
      } else {
        setStatus('photo_rejected');
        setError(od.validation_message || 'Photo does not clearly show an odometer. Please retake.');
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      console.error('[useOdometerCapture] Photo verification error:', err);
      saveForDeferredVerification('Could not reach the odometer verification service. Enter the visible reading manually to continue.', 'api_error_fallback');
    }
  }, [isOffline, reading, vehicleName, verifyPhotoOverride]);

  // ── Tier 3: Manual entry ───────────────────────────────────────
  const setManualReading = useCallback((km: number) => {
    setReading(km);
    if (photo && (verification?.validation === 'pending_offline' || verification?.validation === 'api_error_fallback')) {
      setSource('photo_pending_verification');
    } else {
      setSource('manual');
    }
    setStatus('complete');
  }, [photo, verification]);

  // Live typing variant of setManualReading — keeps status unchanged so
  // the manual input stays mounted, but propagates reading + source so
  // form gating reacts as soon as a valid number is entered.
  const updateManualReading = useCallback((km: number | null) => {
    setReading(km);
    if (km == null) return;
    if (photo && (verification?.validation === 'pending_offline' || verification?.validation === 'api_error_fallback')) {
      setSource('photo_pending_verification');
    } else {
      setSource('manual');
    }
  }, [photo, verification]);

  // ── Reset ──────────────────────────────────────────────────────
  const reset = useCallback(() => {
    setStatus('idle');
    setSource(null);
    setReading(null);
    setIsTracked(false);
    setTrackingProvider(null);
    setReadingIsLive(false);
    setPhoto(null);
    setVerification(null);
    setError(null);
  }, []);

  // ── Auto-fetch on mount ────────────────────────────────────────
  useEffect(() => {
    if (autoFetch && (vehicleId || fetchVehicleInfoOverride)) {
      fetchFromTracking();
    } else if (!vehicleId && !fetchVehicleInfoOverride) {
      // No vehicle ID provided - transition to manual entry immediately
      transitionToManualOrPhoto(null);
    }
  }, [autoFetch, fetchFromTracking, fetchVehicleInfoOverride, isOffline, requirePhotoWhenOnline, transitionToManualOrPhoto, vehicleId]);

  // ── Derived ────────────────────────────────────────────────────
  const loading = ['fetching_tracking', 'verifying_photo'].includes(status);
  const isComplete = reading != null && (
    status === 'tracking_success' ||
    status === 'photo_verified' ||
    status === 'complete'
  );
  const allowManualEntry = status === 'photo_pending_sync'
    || status === 'manual_entry';

  const statusMessage = (() => {
    switch (status) {
      case 'idle': return 'Preparing...';
      case 'fetching_tracking': return 'Fetching odometer from vehicle tracker...';
      case 'tracking_success': return `${readingIsLive ? 'Live' : 'Synced'} reading from ${trackingProvider || 'tracker'}`;
      case 'tracking_failed': return 'Tracker unavailable — photo required';
      case 'awaiting_photo': return 'Take a photo of the odometer';
      case 'verifying_photo': return 'AI verifying odometer photo...';
      case 'photo_verified': return 'Odometer verified from photo';
      case 'photo_pending_sync': return verification?.validation === 'api_error_fallback'
        ? 'Verification service unavailable — enter the visible reading now'
        : 'Photo saved offline — enter the visible reading now';
      case 'photo_rejected': return error || 'Photo rejected — please retake';
      case 'manual_entry': return isOffline
        ? 'Offline mode — enter odometer reading manually'
        : 'Enter odometer reading manually';
      case 'complete': return 'Odometer captured';
      default: return '';
    }
  })();

  // ── Build result ───────────────────────────────────────────────
  const getResult = useCallback((): OdometerCaptureResult | null => {
    if (reading == null || !source) return null;
    return {
      reading,
      source,
      photo: photo || undefined,
      verification: verification || undefined,
      tracked_provider: trackingProvider || undefined,
      live: source === 'tracking' ? readingIsLive : undefined,
    };
  }, [reading, source, photo, verification, trackingProvider, readingIsLive]);

  return {
    status,
    source,
    reading,
    isTracked,
    trackingProvider,
    readingIsLive,
    photo,
    verification,
    statusMessage,
    loading,
    error,
    isComplete,
    allowManualEntry,
    fetchFromTracking,
    submitPhoto,
    setManualReading,
    updateManualReading,
    reset,
    getResult,
  };
}
